import React, { useState, useCallback, useRef, useEffect } from 'react';
import { BulkCameraSession } from './BulkCameraSession';
import { BulkReview } from './BulkReview';
import MultiItemResolver from './MultiItemResolver';
import { ProductData } from '../../types';
import { analyzeProductImagesBatch } from '../../services/geminiService';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';
import { showToast } from '../../utils/helpers';
import { Package, Camera, CheckCircle2, Loader2 } from 'lucide-react';

interface ScannedProduct {
  id: string;
  images: string[];
  productData: ProductData;
}

type BulkFlowState = 'idle' | 'scanning' | 'analyzing' | 'resolving' | 'review' | 'publishing';

function generateId(): string {
  return `prod_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
}

const BulkUploadFlow: React.FC<{ backButton: React.ReactNode }> = ({ backButton }) => {
  const [flowState, setFlowState] = useState<BulkFlowState>('idle');
  const [products, setProducts] = useState<ScannedProduct[]>([]);
  const [analyzingProgress, setAnalyzingProgress] = useState({ current: 0, total: 0 });
  const [analyzingStatus, setAnalyzingStatus] = useState('');
  const [resolvingData, setResolvingData] = useState<{
    imageSrc: string;
    interpretations: ScannedProduct[];
  } | null>(null);
  const canceledRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => { canceledRef.current = true; };
  }, []);

  const handleFilesReady = useCallback(async (files: File[]) => {
    setFlowState('analyzing');
    setAnalyzingProgress({ current: 0, total: files.length });
    setAnalyzingStatus(`Converting ${files.length} images...`);
    canceledRef.current = false;

    // Convert all files to base64 first
    const base64Images: string[] = [];
    for (let i = 0; i < files.length; i++) {
      if (canceledRef.current) break;
      setAnalyzingProgress({ current: i + 1, total: files.length });
      try {
        base64Images.push(await fileToBase64(files[i]));
      } catch {
        console.error(`Failed to convert image ${i + 1}`);
      }
    }

    if (canceledRef.current || base64Images.length === 0) return;

    // SINGLE Gemini call — analyzes all images and groups duplicates in one go
    setAnalyzingStatus(`Analyzing ${base64Images.length} images with AI...`);
    const results = await analyzeProductImagesBatch(base64Images);

    if (canceledRef.current) return;

    const accumulated: ScannedProduct[] = results.map((result) => {
      const images = result.imageIndices.map(idx => base64Images[idx]).filter(Boolean);
      const productData: ProductData = {
        title: result.productData.title || '',
        description: result.productData.description || '',
        price: result.productData.price || 0,
        currency: 'NGN',
        category: result.productData.category || 'General',
        tags: result.productData.tags || [],
        images,
        colors: result.productData.colors || [],
        sizes: result.productData.sizes || [],
        materials: result.productData.materials || [],
        styles: result.productData.styles || [],
        patterns: result.productData.patterns || [],
        brand: result.productData.brand || 'Generic',
        condition: (result.productData.condition as any) || 'New',
        isDigital: result.productData.isDigital || false,
        digitalProductLink: '',
        digitalFileName: '',
        stock: 1,
        variants: [],
        dimensions: result.productData.dimensions || '',
        weight: result.productData.weight || '',
        deliveryOption: 'Standard Delivery',
        deliveryTimeline: '3',
        returnPolicy: '7',
        fileSize: undefined,
        fileType: undefined,
      };
      return { id: generateId(), images, productData };
    });

    // Check if any single image produced multiple products (multi-item image)
    const imageGroups = new Map<number, ScannedProduct[]>();
    accumulated.forEach((product, idx) => {
      const primaryIdx = results[idx]?.imageIndices?.[0] ?? idx;
      const group = imageGroups.get(primaryIdx) || [];
      group.push(product);
      imageGroups.set(primaryIdx, group);
    });

    let conflictImageIndex: number | null = null;
    for (const [imgIdx, group] of imageGroups) {
      if (group.length > 1) {
        conflictImageIndex = imgIdx;
        break;
      }
    }

    if (conflictImageIndex !== null) {
      const group = imageGroups.get(conflictImageIndex)!;
      setResolvingData({
        imageSrc: base64Images[conflictImageIndex],
        interpretations: group,
      });
      setFlowState('resolving');
      return;
    }

    // Merge with existing products (supports "Add More" flow)
    setProducts(prev => {
      const combined = [...prev, ...accumulated];
      return combined.slice(0, 10);
    });
    setAnalyzingStatus('Analysis complete!');
    await new Promise(r => setTimeout(r, 500));
    setFlowState('review');
  }, []);

  const handleResolveMultiItem = useCallback((selected: ScannedProduct | 'collection') => {
    if (!resolvingData) return;

    let chosenProduct: ScannedProduct;
    if (selected === 'collection') {
      const first = resolvingData.interpretations[0];
      const totalPrice = resolvingData.interpretations.reduce((sum, p) => sum + (p.productData.price || 0), 0);
      chosenProduct = {
        ...first,
        id: generateId(),
        productData: {
          ...first.productData,
          title: `${first.productData.title} (Bundle)`,
          description: `Collection bundle: ${resolvingData.interpretations.map(p => p.productData.title).join(', ')}. ${first.productData.description}`,
          price: totalPrice,
        },
      };
    } else {
      chosenProduct = selected;
    }

    const conflictingIds = new Set(resolvingData.interpretations.map(p => p.id));
    setProducts(prev => {
      const filtered = prev.filter(p => !conflictingIds.has(p.id));
      return [...filtered, chosenProduct].slice(0, 10);
    });
    setResolvingData(null);
    setAnalyzingStatus('Analysis complete!');
    setFlowState('review');
  }, [resolvingData]);

  const handleUpdateProduct = useCallback((id: string, data: ProductData) => {
    setProducts(prev => prev.map(p =>
      p.id === id ? { ...p, productData: data } : p
    ));
  }, []);

  const handleRemoveProduct = useCallback((id: string) => {
    setProducts(prev => prev.filter(p => p.id !== id));
  }, []);

  const dataURLtoBlob = (dataURL: string) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) { u8arr[n] = bstr.charCodeAt(n); }
    return new Blob([u8arr], { type: mime });
  };

  const uploadImagesForProduct = async (images: string[]): Promise<string[]> => {
    const uploadedUrls: string[] = [];
    for (const image of images) {
      if (image.startsWith('data:')) {
        try {
          const blob = dataURLtoBlob(image);
          const formData = new FormData();
          formData.append('file', blob, 'product_image.jpg');
          const response = await api.post(API_CONFIG.PRODUCTS.UPLOAD, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (response.data.success) {
            uploadedUrls.push(response.data.url || response.data.fileUrl || response.data.secure_url);
          } else {
            throw new Error(response.data.message || 'Image upload failed');
          }
        } catch (error) {
          console.error('Error uploading image:', error);
          throw new Error('Failed to upload product images');
        }
      } else {
        uploadedUrls.push(image);
      }
    }
    return uploadedUrls;
  };

  const handlePublishAll = async () => {
    setFlowState('publishing');
    let productsWithUrls: any[] = [];
    try {
      showToast('Uploading images...', 'success');
      productsWithUrls = await Promise.all(
        products.map(async (product) => {
          const imageUrls = await uploadImagesForProduct(product.images);
          return {
            name: product.productData.title,
            description: product.productData.description,
            price: product.productData.price,
            category: product.productData.category,
            listing_type: 'product',
            delivery_timeline: product.productData.deliveryTimeline,
            inventory: product.productData.stock,
            brand: product.productData.brand,
            condition: product.productData.condition.toLowerCase(),
            return_policy: product.productData.returnPolicy,
            weight: product.productData.weight || undefined,
            dimensions: product.productData.dimensions || undefined,
            image_urls: imageUrls,
            tags: product.productData.tags,
            is_digital: product.productData.isDigital,
            available_for_pickup: product.productData.isDigital ? undefined : (product.productData.availableForPickup !== false ? 'true' : 'false'),
            available_for_delivery: product.productData.isDigital ? undefined : (product.productData.availableForDelivery !== false ? 'true' : 'false'),
            lagos_delivery_fee: product.productData.isDigital ? undefined : (product.productData.lagosDeliveryFee || 0),
            outside_lagos_delivery_fee: product.productData.isDigital ? undefined : (product.productData.outsideLagosDeliveryFee || 0),
            accept_negotiations: product.productData.isDigital ? false : (product.productData.acceptNegotiations || false),
            min_price: product.productData.acceptNegotiations ? (product.productData.minPrice || 0) : undefined,
            max_price: product.productData.acceptNegotiations ? (product.productData.maxPrice || 0) : undefined,
            upfront_payment_percentage: product.productData.upfrontPaymentPercentage || 0,
            variations: buildVariations(product.productData),
          };
        })
      );

      const response = await api.post('/bulk-upload-products', { products: productsWithUrls });

      const { succeeded, failed, details } = response.data.results || {};
      const total = products.length;

      if (failed > 0) {
        // Show validation errors for failed products
        const errors = (details || [])
          .filter((d: any) => d.status === 'failed')
          .map((d: any) => `${d.productName || `Product #${d.index + 1}`}: ${d.error}`);

        console.error('Bulk upload errors:', errors);
        showToast(
          `${succeeded} of ${total} uploaded. Errors:\n${errors.join('\n')}`,
          'error'
        );

        if (succeeded > 0) {
          setTimeout(() => { window.location.href = '/store#products'; }, 2000);
        } else {
          setFlowState('review');
          return;
        }
      } else {
        showToast(`${succeeded} of ${total} products uploaded successfully!`, 'success');
        setTimeout(() => { window.location.href = '/store#products'; }, 1500);
      }
    } catch (error: any) {
      console.error('Bulk upload error:', error);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      console.error('Results details:', error.response?.data?.results?.details);
      console.error('Request body (first product):', productsWithUrls?.[0]);
      const msg = error.response?.data?.message
        || error.response?.data?.error
        || (typeof error.response?.data === 'string' ? error.response.data : null)
        || error.message
        || 'Failed to upload products';
      showToast(msg, 'error');
      setFlowState('review');
    }
  };

  const buildVariations = (data: ProductData): string => {
    const variations: any[] = [];
    if (data.colors?.length > 0) {
      data.colors.forEach(color => {
        variations.push({ type: 'Color', value: color, price: 0, inventory: Math.max(1, Math.floor(data.stock / data.colors.length)) });
      });
    }
    if (data.sizes?.length > 0) {
      data.sizes.forEach(size => {
        variations.push({ type: 'Size', value: size, price: 0, inventory: Math.max(1, Math.floor(data.stock / data.sizes.length)) });
      });
    }
    return variations.length > 0 ? JSON.stringify(variations) : '';
  };

  // --- Idle Screen ---
  if (flowState === 'idle') {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-6 p-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
        {backButton}
        <div className="w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
          <Package className="w-10 h-10 text-emerald-600" />
        </div>
        <div className="text-center max-w-sm">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Bulk Product Upload</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
            Scan up to 10 products at once. Capture multiple photos, then our AI will analyze and auto-group duplicate products.
          </p>
        </div>
        <button
          onClick={() => setFlowState('scanning')}
          className="px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl transition-colors text-base flex items-center gap-2"
        >
          <Camera className="w-5 h-5" /> Start Scanning
        </button>
      </div>
    );
  }

  // --- Scanning ---
  if (flowState === 'scanning') {
    const remaining = Math.max(0, 10 - products.length);
    return (
      <div className="h-full">
        <BulkCameraSession
          onFilesReady={handleFilesReady}
          maxProducts={remaining || 1}
          onBack={() => products.length > 0 ? setFlowState('review') : setFlowState('idle')}
          autoStart
        />
      </div>
    );
  }

  // --- Batch Analyzing ---
  if (flowState === 'analyzing') {
    const { current, total } = analyzingProgress;
    const percent = total > 0 ? Math.round((current / total) * 100) : 0;
    return (
      <div className="flex flex-col h-full items-center justify-center gap-6 p-6">
        {backButton}
        <div className="flex flex-col items-center gap-6 text-center max-w-sm">
          <div className="w-20 h-20 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Analyzing Products</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{analyzingStatus}</p>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-blue-600 h-full rounded-full transition-all duration-300 ease-out"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-500">{current} of {total} images processed</p>

          {/* Status items */}
          <div className="w-full space-y-2 text-left">
            {Array.from({ length: Math.min(current, total) }).map((_, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                <span className="truncate">Image {i + 1}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- Resolving Multi-Item Conflicts ---
  if (flowState === 'resolving' && resolvingData) {
    return (
      <MultiItemResolver
        imageSrc={resolvingData.imageSrc}
        interpretations={resolvingData.interpretations}
        onSelect={handleResolveMultiItem}
        onCancel={() => setFlowState('review')}
      />
    );
  }

  // --- Review ---
  if (flowState === 'review') {
    return (
      <div className="flex flex-col h-full">
        {backButton}
        <div className="flex-1 flex flex-col p-4">
          <BulkReview
            products={products}
            onUpdateProduct={handleUpdateProduct}
            onRemoveProduct={handleRemoveProduct}
            onAddMore={() => setFlowState('scanning')}
            onPublishAll={handlePublishAll}
            onBack={() => setFlowState('scanning')}
            isPublishing={flowState === 'publishing'}
          />
        </div>
      </div>
    );
  }

  // --- Publishing ---
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
      <p className="text-gray-600 dark:text-gray-400 font-medium">Uploading and publishing your products...</p>
    </div>
  );
};

// Helper: File to base64
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default BulkUploadFlow;
